import React, { useEffect } from "react";
import { Shield, Lock, Database, Key, Mail, Clock, AlertCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const PrivacyPolicy = () => {
  const controls = useAnimation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  const policySections = [
    {
      icon: <Shield className="w-6 h-6 text-yellow-400 text-center" />,
      title: "Who We Are",
      content: (
        <p className="text-slate-600 leading-relaxed">
          Our website address is:{" "}
          <a
            href="https://rakfort.com"
            className="text-yellow-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://rakfort.com
          </a>
        </p>
      )
    },
    {
      icon: <AlertCircle className="w-6 h-6 text-yellow-400" />,
      title: "Comments & Media",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            When visitors leave comments or upload media, we collect data shown in the forms, 
            along with the visitor's IP address and browser user agent string for spam detection.
          </p>
          <p className="text-slate-600 leading-relaxed">
            For images, we recommend removing embedded location data (EXIF GPS) as visitors can 
            download and extract this data.
          </p>
        </div>
      )
    },
    {
      icon: <Key className="w-6 h-6 text-yellow-400" />,
      title: "Cookies",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            We use cookies to enhance your experience. These may store your login information, 
            display preferences, and form data for your convenience.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Login cookies last for two days, screen options for a year, and "Remember Me" extends 
            login for two weeks.
          </p>
        </div>
      )
    },
    {
      icon: <Database className="w-6 h-6 text-yellow-400" />,
      title: "Embedded Content & Data Sharing",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Articles may include embedded content (e.g., videos, images) which behaves as if you 
            visited the source website. These sites may collect data, use cookies, and monitor 
            your interaction.
          </p>
          <p className="text-slate-600 leading-relaxed">
            We only share your data when necessary (e.g., password reset requests) and never sell 
            your information to third parties.
          </p>
        </div>
      )
    },
    {
      icon: <Lock className="w-6 h-6 text-yellow-400" />,
      title: "Data Retention & Your Rights",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            We retain comments and their metadata indefinitely. Registered users can view, edit, 
            or delete their personal information at any time (except username).
          </p>
          <p className="text-slate-600 leading-relaxed">
            You have the right to request an exported file of your personal data or request its 
            deletion, except where we're required to retain it for legal or security purposes.
          </p>
        </div>
      )
    },
    {
      icon: <Clock className="w-6 h-6 text-yellow-400" />,
      title: "Policy Updates",
      content: (
        <p className="text-slate-600 leading-relaxed">
          This policy was last updated on November 11, 2023. We may update this privacy policy 
          from time to time. We will notify you of any changes by posting the new privacy policy 
          on this page.
        </p>
      )
    }
  ];

  return (
    <div className="bg-white text-[#2c2c2c]">
      <div className="max-w-[1140px] mx-auto px-5 pt-8 pb-2">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#005a9c] hover:text-[#004a82]">
          <ArrowLeft className="w-4 h-4" />
          Home
        </Link>
      </div>
      <section className="border-b border-[#e8e8e8] bg-[#f0f7ff] py-10 md:py-12">
        <div className="max-w-[1140px] mx-auto px-5">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#737373] mb-2">Legal</p>
          <h1 className="text-2xl md:text-[1.75rem] font-semibold text-[#2c2c2c] tracking-tight">Privacy policy</h1>
          <p className="mt-3 text-[#5c5c5c] max-w-2xl text-[0.95rem]">
            We are committed to protecting your personal information and being transparent about how we handle it.
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="relative py-12 px-5 md:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="space-y-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            {policySections.map((section, index) => (
              <motion.div 
                key={index}
                className="border border-[#e8e8e8] rounded-sm p-6 md:p-8 bg-[#f0f7ff]"
                variants={fadeInUp}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4">
                  <div className="p-2 bg-white border border-[#e8e8e8] rounded-sm shrink-0">
                    {React.cloneElement(section.icon, { className: 'w-6 h-6 text-[#005a9c]' })}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#2c2c2c]">{section.title}</h2>
                    <div className="mt-3 text-[0.9375rem] text-[#5c5c5c]">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contact Information */}
          <motion.div className="mt-16 text-center border border-[#e8e8e8] rounded-sm p-8 bg-[#f0f7ff]" variants={fadeInUp}>
            <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">Questions</h3>
            <p className="text-[#5c5c5c] text-sm max-w-xl mx-auto mb-4">
              If you have any questions about our privacy practices or this policy, contact us at:
            </p>
            <a href="mailto:info@rakfort.com" className="inline-flex items-center gap-2 text-[#005a9c] font-medium hover:text-[#004a82]">
              <Mail className="w-4 h-4" />
              info@rakfort.com
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <div className="max-w-5xl mx-auto px-5 pb-12 text-center text-sm text-[#737373]">
        <p>Last updated: November 11, 2023</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;